import React, { useEffect, useState } from 'react';
import { Alert, Form, Button, Card, FilterGrid } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import activityService from '../../../services/activityService';
import { Activity } from '../../../types/Activity';
import { getForemanAreaOptions } from '../utils/reportFilterOptions';
import { taskPageUrl } from '../../work-id/utils/taskPageUrl';
import reportService, { OverdueTasksPreviewData } from '../../../services/reportService';
import ReportParametersCard from '../components/ReportParametersCard';
import ReportPreviewTable from '../components/ReportPreviewTable';
import Toast from '../../../components/Toast';
import { ServiceArea } from '../../../services/areasService';
import { formatDate as formatPacificDate } from '../../../utils/displayDateTime';

interface OverdueTasksReportViewProps {
  serviceAreas: ServiceArea[];
  loadingServiceAreas: boolean;
  defaultServiceAreaId?: string;
}

const OverdueTasksReportView: React.FC<OverdueTasksReportViewProps> = ({
  serviceAreas,
  loadingServiceAreas,
  defaultServiceAreaId
}) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    serviceArea: defaultServiceAreaId || '',
    foremanAreaIds: [] as string[],
    activityIds: [] as string[],
    search: ''
  });
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const foremanAreaOptions = getForemanAreaOptions(serviceAreas, filters.serviceArea);

  // Activities are listed per service area; with "All Service Areas" the list would be every
  // activity in the system, so the filter waits for a service area instead.
  useEffect(() => {
    if (!filters.serviceArea) {
      setActivities([]);
      return;
    }
    let cancelled = false;
    setLoadingActivities(true);
    activityService
      .getActivitiesByServiceArea(Number(filters.serviceArea))
      .then((list) => {
        if (!cancelled) setActivities(list);
      })
      .catch(() => {
        if (!cancelled) setActivities([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingActivities(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters.serviceArea]);

  const filterParams = () => ({
    serviceArea: filters.serviceArea || undefined,
    foremanArea: filters.foremanAreaIds.length ? filters.foremanAreaIds.join(',') : undefined,
    activity: filters.activityIds.length ? filters.activityIds.join(',') : undefined,
    search: filters.search.trim() || undefined
  });
  const [previewData, setPreviewData] = useState<OverdueTasksPreviewData[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    onPageChange: (page: number, pageSize?: number) => {}
  });
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [showToast, setShowToast] = useState(false);
  const [sortField, setSortField] = useState<string>('plannedCompletionDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  // Server-side pagination helper
  const handlePageChange = (page: number, pageSize?: number) => {
    handleGeneratePreview(page, pageSize);
  };

  // Handle page size changes
  const handlePageSizeChange = (newPageSize: number) => {
    handleGeneratePreview(1, newPageSize);
  };

  const handleGeneratePreview = async (
    page: number = 1,
    pageSize?: number,
    // Passed explicitly by handleSort: the sort state set there hasn't landed yet when this runs.
    sort: { field: string; direction: 'asc' | 'desc' } = { field: sortField, direction: sortDirection }
  ) => {
    setLoadingPreview(true);
    setErrorMessage(null);
    try {
      const response = await reportService.getOverdueTasksPreviewData({
        ...filterParams(),
        page,
        limit: pageSize || pagination.limit,
        sortField: sort.field || 'plannedCompletionDate',
        sortDirection: sort.direction
      });
      setPreviewData(response.data);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages,
        onPageChange: handlePageChange
      });
    } catch (e) {
      setErrorMessage('Failed to generate overdue tasks report preview.');
      setPreviewData([]);
      setPagination({ page: 1, limit: 50, total: 0, totalPages: 0, onPageChange: handlePageChange });
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSort = (field: string) => {
    const newDirection = field === sortField && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    handleGeneratePreview(1, undefined, { field, direction: newDirection });
  };

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      await reportService.generateOverdueTasksReport(filterParams());
      setToastMessage('Overdue Tasks report exported successfully!');
      setShowToast(true);
    } catch (e) {
      setErrorMessage('Failed to export overdue tasks report.');
    } finally {
      setLoadingExport(false);
    }
  };

  const handleRowClick = (row: OverdueTasksPreviewData) => {
    // The report covers every task type, but QP Tracking only lists Quantified tasks: open each
    // task on its own page (Routine Work, QP Tracking, or All Tasks for everything else).
    navigate(taskPageUrl({ id: row.id, type: row.type }));
  };

  // V3-007: the shared formatter – a date-only Planned Completion Date is never shifted a day.
  const formatDate = (dateString: string | null) => formatPacificDate(dateString);

  const columns = [
    { 
      label: 'Task ID', 
      key: 'taskNumber', 
      sortable: true, 
      width: '120px',
      render: (row: OverdueTasksPreviewData) => row.taskNumber || '-'
    },
    { 
      label: 'Task Name', 
      key: 'name', 
      sortable: false, 
      width: '200px',
      render: (row: OverdueTasksPreviewData) => row.name || '-'
    },
    { 
      label: 'Status', 
      key: 'status', 
      sortable: true, 
      width: '120px'
    },
    { 
      label: 'Planned Completion Date', 
      key: 'plannedCompletionDate', 
      sortable: true, 
      width: '180px',
      render: (row: OverdueTasksPreviewData) => formatDate(row.plannedCompletionDate)
    },
    { 
      label: 'Days Overdue', 
      key: 'daysOverdue', 
      sortable: true, 
      width: '120px',
      render: (row: OverdueTasksPreviewData) => {
        if (row.daysOverdue === null) return '-';
        return (
          <span style={{ color: row.daysOverdue > 0 ? '#dc3545' : 'inherit', fontWeight: 'bold' }}>
            {row.daysOverdue}
          </span>
        );
      }
    },
    { 
      label: 'Type', 
      key: 'type', 
      sortable: true, 
      width: '120px'
    },
    { 
      label: 'Priority', 
      key: 'priority', 
      sortable: true, 
      width: '100px',
      render: (row: OverdueTasksPreviewData) => row.priority || '-'
    },
    { 
      label: 'Project', 
      key: 'projectName', 
      sortable: false, 
      width: '200px',
      render: (row: OverdueTasksPreviewData) => row.projectName || '-'
    },
    { 
      label: 'Service Area', 
      key: 'serviceArea', 
      sortable: false, 
      width: '150px',
      render: (row: OverdueTasksPreviewData) => row.serviceArea || '-'
    },
    { 
      label: 'Foreman Area', 
      key: 'foremanArea', 
      sortable: false, 
      width: '150px',
      render: (row: OverdueTasksPreviewData) => row.foremanArea || '-'
    },
    { 
      label: 'Activity Number', 
      key: 'activityNumber', 
      sortable: false, 
      width: '130px',
      render: (row: OverdueTasksPreviewData) => row.activityNumber || '-'
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexShrink: 0 }}>
        {errorMessage && <Alert variant="danger" className="tw-mb-3">{errorMessage}</Alert>}

        <ReportParametersCard
          onGenerate={() => handleGeneratePreview(1)}
          onExport={handleExport}
          loadingGenerate={loadingPreview}
          loadingExport={loadingExport}
          disableExport={!pagination.total || loadingPreview}
          // Export lives on the results header, as on every other report (R-03).
          hideExportButton
          generateButtonText="Run Report"
          defaultCollapsed={false}
          onCollapseChange={setIsFiltersCollapsed}
        >
          <FilterGrid>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Service Area</Form.Label>
              <Form.Select
                size="sm"
                value={filters.serviceArea}
                // Foreman areas and activities belong to a service area, so a new area clears them.
                onChange={(e) => setFilters({ ...filters, serviceArea: e.target.value, foremanAreaIds: [], activityIds: [] })}
                disabled={loadingServiceAreas}
              >
                <option value="">All Service Areas</option>
                {serviceAreas.map((area) => (
                  <option key={area.id} value={area.id}>{area.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Foreman Area</Form.Label>
              <MultiSelectDropdown
                options={foremanAreaOptions}
                value={filters.foremanAreaIds}
                onChange={(selected) => setFilters({ ...filters, foremanAreaIds: selected.map(String) })}
                formatOption={(area) => area.name}
                getValue={(area) => String(area.id)}
                placeholder="All Foreman Areas"
                noOptionsMessage="No foreman areas"
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Activity</Form.Label>
              <MultiSelectDropdown
                options={activities}
                value={filters.activityIds}
                onChange={(selected) => setFilters({ ...filters, activityIds: selected.map(String) })}
                formatOption={(activity) =>
                  activity.activityNumber ? `${activity.activityNumber} - ${activity.activityName}` : activity.activityName
                }
                formatDisplay={(activity) => activity.activityNumber || activity.activityName}
                getValue={(activity) => String(activity.id)}
                placeholder={filters.serviceArea ? 'All Activities' : 'Select a service area first'}
                disabled={!filters.serviceArea || loadingActivities}
                noOptionsMessage="No activities"
              />
            </Form.Group>
            <Form.Group className="tw-mb-1">
              <Form.Label className="tw-text-sm tw-mb-1">Search</Form.Label>
              <Form.Control
                size="sm"
                type="search"
                placeholder="Task number or name"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleGeneratePreview(1);
                }}
              />
            </Form.Group>
          </FilterGrid>
        </ReportParametersCard>
      </div>

      {/* Info Alert */}
      <div className="tw-mt-2 tw-mb-2">
        <Alert variant="info" className="tw-py-2 tw-px-3 tw-mb-0">
          <strong>Report Criteria:</strong> This report shows tasks of every type (Quantified, Routine and others) with status <strong>Planned</strong> or <strong>Active</strong> that have a Planned Completion Date that is past today's date. Click a row to open the task on its own page.
        </Alert>
      </div>


      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
        <Card style={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minWidth: 0 }}>
          <Card.Header className="tw-flex tw-justify-between tw-items-center" style={{ flexShrink: 0 }}>
            <h6 className="tw-mb-0">Overdue Tasks ({pagination.total} records)</h6>
            {pagination.total > 0 && (
              <Button variant="outline-primary" size="sm" onClick={handleExport} disabled={loadingExport}>
                <i className="bi bi-download tw-mr-1"></i>
                {loadingExport ? 'Exporting...' : 'Export Report'}
              </Button>
            )}
          </Card.Header>
          {/* ReportPreviewTable owns its own scroll region (single mechanism, see
              ReportPreviewTable.tsx); no extra overflow wrapper needed here. */}
          <Card.Body className="tw-p-0" style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              <ReportPreviewTable
                columns={columns}
                data={previewData}
                pagination={{
                  ...pagination,
                  onPageChange: handlePageChange,
                  onPageSizeChange: handlePageSizeChange
                }}
                loading={loadingPreview}
                onSort={handleSort}
                sortField={sortField}
                sortDirection={sortDirection}
                onRowClick={handleRowClick}
              />
          </Card.Body>
        </Card>
      </div>

      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type="success"
      />
    </div>
  );
};

export default OverdueTasksReportView;
